/**
 * Availability API wrapper (v2 — Project Independence, DNKADM-0061 U2).
 *
 * Backs the create-on-behalf + reschedule **slot picker** (handoff §7): given a service
 * + variation + location and a date range, the server returns the bookable slots
 * (already honoring the service's assignment modes, capacity, operating hours, and
 * booking window). The client renders ONLY the returned slots — it never recomputes
 * availability locally (that was the v1 client-side model; the owned v2 backend is the
 * authority).
 *
 * Bearer-only `/v2/**` surface — `request()` routes it (DNKADM-0049 `isV2Surface`):
 * bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry. (Availability is a
 * read, so the mutating `X-Session-Mode` stamp does not apply.)
 *
 * **Endpoint (handoff §7):** `POST /v2/availability` ←
 * `{ serviceId, variationId, locationId, startDate, endDate, staffId?, resourceId? }`
 * → `{ …, days: [ { date, slots: [ { start, end, staffOptions?, resourceOptions?,
 * resourceCapacityRemaining? } ] } ] }`. Slot `start`/`end` are ISO-8601 WITH OFFSET
 * (rendered as-is). `ROUND_ROBIN` omits the per-slot options. Every read is parsed
 * DEFENSIVELY (mirrors `services.ts`): a missing/wrong-typed field degrades, a slot
 * without a usable `start` is dropped, never a throw.
 *
 * **Wire-shape assumptions (DNKADM-0061 delivery Notes; live OpenAPI authoritative,
 * §10):** the `days`/`slots` envelope + field names follow the handoff verbatim;
 * `staffOptions`/`resourceOptions` are arrays of `{ id, name? }` (or bare id strings).
 */
import { request } from './client';

/** A selectable staff or resource option attached to a slot (`MEMBER_SELECTS`). */
export interface SlotOption {
  /** The option id (a `v2_staff.id` or `v2_resource.id`). */
  id: string;
  /** Display name, or null when the wire omitted it (→ the id is shown). */
  name: string | null;
}

/** One bookable slot in a day (handoff §7). `start`/`end` are ISO-8601 with offset. */
export interface AvailabilitySlot {
  /** Slot start — ISO-8601 WITH OFFSET (rendered as-is; the value POSTed as `start`). */
  start: string;
  /** Slot end — ISO-8601 WITH OFFSET, or null. */
  end: string | null;
  /** Selectable staff (`MEMBER_SELECTS` staff); empty/absent → no picker for this slot. */
  staffOptions: SlotOption[];
  /** Selectable resources (`MEMBER_SELECTS` resource); empty/absent → no picker. */
  resourceOptions: SlotOption[];
  /** Remaining capacity on the slot's resource (when the server supplies it), or null. */
  resourceCapacityRemaining: number | null;
  /**
   * DNKADM-0443 / DNKAPI-0649: the ADMIN availability route also returns slots that sit
   * inside the service's booking notice (lead time), each marked `insideLeadTime: true`.
   * Absent or non-`true` → false (the member route never sends it).
   */
  insideLeadTime: boolean;
}

/** One day of availability (handoff §7). `date` is the local `YYYY-MM-DD`. */
interface AvailabilityDay {
  date: string | null;
  slots: AvailabilitySlot[];
}

/** The parsed `POST /v2/availability` response. */
export interface Availability {
  days: AvailabilityDay[];
}

/** Request params for {@link getAvailability} (handoff §7). */
export interface AvailabilityParams {
  serviceId: string;
  variationId: string;
  locationId: string;
  /** Inclusive range start, local `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive range end, local `YYYY-MM-DD`. */
  endDate: string;
  /** Pre-filter to a staff (`MEMBER_SELECTS`); omitted otherwise. */
  staffId?: string;
  /** Pre-filter to a resource (`MEMBER_SELECTS`); omitted otherwise. */
  resourceId?: string;
}

/**
 * DNKADM-0443 ASSUMED wire name (DNKAPI-0649, not yet on dink-api main when written): the
 * per-slot boolean on the admin availability response. The request-side `staffId` is already
 * in {@link availabilityBody}. Change the name HERE if the live wire differs.
 */
const INSIDE_LEAD_TIME_FIELD = 'insideLeadTime';

// --- Defensive parsing helpers (mirrors services.ts) ---------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): a slot option's
 * `v2_staff.id`/`v2_resource.id` arrives as a number on the wire and is normalized
 * to the `string` the picker keys off — a finite number → `String(n)`, a non-empty
 * string → trimmed, else null. Preserves genuinely-string ids (accepts both).
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse a slot option from `{ id, name? }` or a bare id string. Returns null when no
 * usable id (it can't be selected), so the picker never offers a half-identified row.
 */
function parseOption(raw: unknown): SlotOption | null {
  if (typeof raw === 'string' || typeof raw === 'number') {
    const id = asId(raw);
    return id ? { id, name: null } : null;
  }
  if (isObject(raw)) {
    // v2 staff/resource fk ids — numeric on the wire (DNKADM-0064).
    const id = asId(raw['id']) ?? asId(raw['staffId']) ?? asId(raw['resourceId']);
    if (id === null) return null;
    return { id, name: asText(raw['name']) };
  }
  return null;
}

/** Parse a slot's option array (tolerating absent / non-array → `[]`). */
function parseOptions(raw: unknown): SlotOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseOption).filter((o): o is SlotOption => o !== null);
}

/** Parse one slot, or null when it has no usable `start` (it can't be selected). */
function parseSlot(raw: unknown): AvailabilitySlot | null {
  if (!isObject(raw)) return null;
  const start = asText(raw['start']) ?? asText(raw['startTime']);
  if (start === null) return null;
  return {
    start,
    end: asText(raw['end']) ?? asText(raw['endTime']),
    staffOptions: parseOptions(raw['staffOptions']),
    resourceOptions: parseOptions(raw['resourceOptions']),
    resourceCapacityRemaining: asNumber(raw['resourceCapacityRemaining']),
    insideLeadTime: raw[INSIDE_LEAD_TIME_FIELD] === true,
  };
}

/** Parse one day (defensively); a non-array `slots` degrades to `[]`. */
function parseDay(raw: unknown): AvailabilityDay {
  const o = isObject(raw) ? raw : {};
  const slots = Array.isArray(o['slots']) ? o['slots'] : [];
  return {
    date: asText(o['date']),
    slots: slots.map(parseSlot).filter((s): s is AvailabilitySlot => s !== null),
  };
}

/** Tolerantly extract the `days` array (bare array or `{ days }`/`{ content }`). */
function extractDays(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['days'])) return body['days'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/**
 * `POST /v2/availability` → the bookable slots for the service/variation/location over
 * the date range, parsed defensively (render ONLY the returned slots). The optional
 * `staffId`/`resourceId` pre-filter (`MEMBER_SELECTS`) are sent only when set. Throws a
 * typed `ApiError` on a non-2xx response.
 */
/** The request body both availability routes take. */
function availabilityBody(params: AvailabilityParams): Record<string, unknown> {
  const json: Record<string, unknown> = {
    serviceId: params.serviceId,
    variationId: params.variationId,
    locationId: params.locationId,
    startDate: params.startDate,
    endDate: params.endDate,
  };
  if (params.staffId !== undefined) json['staffId'] = params.staffId;
  if (params.resourceId !== undefined) json['resourceId'] = params.resourceId;
  return json;
}

/**
 * `POST /v2/availability` — the MEMBER route: the access window is the CALLER's own
 * contact. A staff login has no member contact, so dink-api answers `TENANT_NOT_FOUND`
 * (DNKADM-0390). The console no longer calls this; it stays for the member-app contract only.
 */
export async function getAvailability(params: AvailabilityParams): Promise<Availability> {
  const body = await request<unknown>('POST', '/v2/availability', { json: availabilityBody(params) });
  return { days: extractDays(body).map(parseDay) };
}

/**
 * `POST /v2/admin/members/{contactId}/availability` — the ADMIN route (dink-api
 * `AdminAvailabilityController`, DNKAPI-0430; DNKADM-0390 U1): the same request, engine and
 * response as the member route, but the access window comes from the TARGET member in the
 * path. OWNER/ADMIN-gated and tenant-scope-guarded server-side: an unknown or foreign contact
 * fails closed (`404`), a non-admin caller gets `403`. Bearer-only `/v2/admin/**` surface.
 */
export async function getMemberAvailability(
  contactId: string,
  params: AvailabilityParams,
): Promise<Availability> {
  const body = await request<unknown>(
    'POST',
    `/v2/admin/members/${encodeURIComponent(contactId)}/availability`,
    { json: availabilityBody(params) },
  );
  return { days: extractDays(body).map(parseDay) };
}
